import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import InputOTP from '@/components/InputOTP';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useCompleteMfa } from '@/models/user';

import { useCompleteLogin } from '../useCompleteLogin';

export const MfaStep = ({
  mfaToken,
  onMfaExpired,
}: {
  mfaToken: string;
  onMfaExpired: () => void;
}) => {
  const { t } = useLingui();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const { mutateAsync: completeMfa, isPending: isCompleteMfaPending } = useCompleteMfa();

  const completeLogin = useCompleteLogin();

  const handleVerify = () => {
    if (!code || code.length !== 6) {
      setError(
        t({
          id: 'login.mfa-email-form.error.no-code',
          message: 'Please insert a 6-digit code',
        })
      );

      return;
    }

    completeMfa({ mfaMethod: 'email', mfaToken, code })
      .then(completeLogin)
      .catch((err) => {
        if (err.message.includes('Expired code')) {
          onMfaExpired();
        } else if (err.message.includes('Invalid code')) {
          setError(
            t({
              id: 'login.mfa-email-form.error.invalid-code',
              message: 'The code is invalid, please try again',
            })
          );
        } else {
          console.error(err);
          setError(
            (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
          );
        }
      });
  };

  return (
    <Stack gap="1rem" alignItems="center">
      <Title size="200" as="h1">
        <Trans id="login.mfa-email-form.title">2-step verification</Trans>
      </Title>
      <Paragraph align="center">
        <Trans id="login.mfa-email-form.text">
          We have just sent a 6-digit verification code to your email.
          <br />
          Please insert it below:
        </Trans>
      </Paragraph>
      <InputOTP value={code} onChange={setCode} onComplete={handleVerify} />
      <Button variant="primary" onPress={handleVerify} isLoading={isCompleteMfaPending}>
        <Trans id="login.mfa-email-form.verify">Verify</Trans>
      </Button>
      {!!error && <Message type="error">{error}</Message>}
    </Stack>
  );
};
